import { useEffect, useMemo, useState } from "react";
import { RadioItem } from "@/components/common/list/radioList";
import Select, { SelectBaseProps } from "@/components/common/select/select";

export type ReminderTimeOption = {
  label: string;
  value: number;
};

const HOURS_A_DAY = 24;
const HOURS_A_WEEK = 7 * HOURS_A_DAY;

export enum ReminderOptionKeys {
  TWO_HOURS = "2_hours",
  FOUR_HOURS = "4_hours",
  EIGHT_HOURS = "8_hours",
  TWELVE_HOURS = "12_hours",
  TWENTY_FOUR_HOURS = "24_hours",
  FORTY_EIGHT_HOURS = "48_hours",
  THREE_DAYS = "3_days",
  FOUR_DAYS = "4_days",
  FIVE_DAYS = "5_days",
  SIX_DAYS = "6_days",
  ONE_WEEK = "1_week",
  TWO_WEEKS = "2_weeks",
}

const ReminderOptions = {
  [ReminderOptionKeys.TWO_HOURS]: { label: "2 hours", value: 2 },
  [ReminderOptionKeys.FOUR_HOURS]: { label: "4 hours", value: 4 },
  [ReminderOptionKeys.EIGHT_HOURS]: { label: "8 hours", value: 8 },
  [ReminderOptionKeys.TWELVE_HOURS]: { label: "12 hours", value: 12 },
  [ReminderOptionKeys.TWENTY_FOUR_HOURS]: { label: "24 hours", value: 24 },
  [ReminderOptionKeys.FORTY_EIGHT_HOURS]: { label: "48 hours", value: 48 },
  [ReminderOptionKeys.THREE_DAYS]: {
    label: "3 days",
    value: 3 * HOURS_A_DAY,
  },
  [ReminderOptionKeys.FOUR_DAYS]: { label: "4 days", value: 4 * HOURS_A_DAY },
  [ReminderOptionKeys.FIVE_DAYS]: { label: "5 days", value: 5 * HOURS_A_DAY },
  [ReminderOptionKeys.SIX_DAYS]: { label: "6 days", value: 6 * HOURS_A_DAY },
  [ReminderOptionKeys.ONE_WEEK]: { label: "1 week", value: HOURS_A_WEEK },
  [ReminderOptionKeys.TWO_WEEKS]: {
    label: "2 weeks",
    value: 2 * HOURS_A_WEEK,
  },
};

export const pickReminderTimeOptions = (options: ReminderOptionKeys[]) => {
  return options.map((key) => ReminderOptions[key]);
};

export const getReminderTimeOptionsSlice = (
  startOption: ReminderOptionKeys,
  endOption?: ReminderOptionKeys
): ReminderTimeOption[] => {
  const keys = Object.keys(ReminderOptions);
  const startIndex = keys.indexOf(startOption);
  const endIndex = endOption ? keys.indexOf(endOption) + 1 : keys.length;

  return keys.slice(startIndex, endIndex).map((key) => ReminderOptions[key]);
};

const ALL_OPTIONS: ReminderTimeOption[] = Object.values(ReminderOptions);

const DEFAULT_OPTION = ALL_OPTIONS[5].value;

export const getReminderTimeLabel = (value: number) =>
  ALL_OPTIONS.find((option) => option.value === value).label;

export type SelectReminderTimeProps = SelectBaseProps & {
  value: number;
  defaultValue?: number;
  options?: ReminderTimeOption[];
  onSelect: (value: number) => void;
};

export default function SelectReminderTime({
  value,
  defaultValue = DEFAULT_OPTION,
  options = ALL_OPTIONS,
  onSelect,
  ...selectProps
}: SelectReminderTimeProps) {
  const selectedValue = useMemo(
    () => (value ? String(value) : String(defaultValue)),
    [value, defaultValue]
  );
  const [currentValue, setCurrentValue] = useState(selectedValue);

  useEffect(() => {
    setCurrentValue(selectedValue);
  }, [selectedValue]);

  const items: RadioItem[] = options.map((option) => {
    const id = String(option.value);

    return {
      id,
      label: option.label,
      onChange: (id) => setCurrentValue(id),
      isChecked: id === currentValue,
    };
  });

  return (
    <Select
      title="Time prior to appointment"
      items={items}
      selectedValue={items.find((item) => item.id === selectedValue).label}
      onClose={() => setCurrentValue(selectedValue)}
      onSubmit={() => onSelect(Number(currentValue))}
      {...selectProps}
    />
  );
}
